import React, {
    useMemo,
    useState,
} from 'react'

import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Image,
    Input,
    message,
    Modal,
    Popconfirm,
    Row,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    Upload,
} from 'antd'

import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    UploadOutlined,
} from '@ant-design/icons'

import axios from 'axios'

import UploadService from 'services/UploadService'

import {
    env,
} from 'configs/EnvironmentConfig'

const {
    Text,
} = Typography

const API_BASE_URL =
    env.API_ENDPOINT_URL

const getImageUrl = (
    url,
) => {
    if (!url) {
        return null
    }

    if (
        url.startsWith(
            'http',
        )
    ) {
        return url
    }

    return `${API_BASE_URL}${url}`
}

const CardsTab = ({
    selectedGameId,
    config,
    cards = [],
    axiosConfig,
    reload,
    updateConfigLocal,
}) => {
    const [
        cardForm,
    ] = Form.useForm()

    const [
        cardModalOpen,
        setCardModalOpen,
    ] = useState(false)

    const [
        editingCard,
        setEditingCard,
    ] = useState(null)

    const [
        savingCard,
        setSavingCard,
    ] = useState(false)

    const [
        uploadingCard,
        setUploadingCard,
    ] = useState(false)

    const [
        uploadingBack,
        setUploadingBack,
    ] = useState(false)

    const [
        savingBack,
        setSavingBack,
    ] = useState(false)

    const requiredCards =
        Number(
            config?.pairCount ||
            4,
        )

    const totalBoardCards =
        requiredCards *
        2

    const activeCards =
        useMemo(
            () =>
                cards.filter(
                    (card) =>
                        card.isActive !==
                        false,
                ),
            [
                cards,
            ],
        )

    const canAddCard =
        activeCards.length <
        requiredCards

    // ======================================================
    // VERSO DAS CARTAS
    // ======================================================

    const saveBackImage =
        async (
            imageUrl,
        ) => {
            if (
                !selectedGameId
            ) {
                return
            }

            try {
                setSavingBack(
                    true,
                )

                const response =
                    await axios.put(
                        `${API_BASE_URL}/memory-games/${selectedGameId}/config`,

                        {
                            cardBackImageUrl:
                                imageUrl ||
                                null,
                        },

                        axiosConfig,
                    )

                updateConfigLocal?.(
                    response.data,
                )

                message.success(
                    imageUrl
                        ? 'Verso das cartas atualizado.'
                        : 'Imagem do verso removida.',
                )

                await reload?.()
            } catch (error) {
                console.error(
                    'Erro ao salvar verso:',
                    error,
                )

                message.error(
                    error?.response?.data?.message ||
                    'Não foi possível salvar o verso das cartas.',
                )
            } finally {
                setSavingBack(
                    false,
                )
            }
        }

    const uploadBackImage =
        async ({
            file,
            onSuccess,
            onError,
        }) => {
            try {
                setUploadingBack(
                    true,
                )

                const response =
                    await UploadService.image(
                        file,
                    )

                const data =
                    response?.data ||
                    response

                if (
                    !data?.url
                ) {
                    throw new Error(
                        'UPLOAD_URL_NOT_RETURNED',
                    )
                }

                await saveBackImage(
                    data.url,
                )

                onSuccess?.(
                    data,
                )
            } catch (error) {
                console.error(
                    'Erro no upload do verso:',
                    error,
                )

                message.error(
                    'Erro ao enviar imagem do verso.',
                )

                onError?.(
                    error,
                )
            } finally {
                setUploadingBack(
                    false,
                )
            }
        }

    // ======================================================
    // MODAL CARTA
    // ======================================================

    const openNewCard =
        () => {
            if (
                !canAddCard
            ) {
                message.warning(
                    `Este jogo já possui as ${requiredCards} imagens necessárias.`,
                )

                return
            }

            setEditingCard(
                null,
            )

            cardForm.resetFields()

            cardForm.setFieldsValue({
                name:
                    '',

                imageUrl:
                    '',

                isActive:
                    true,
            })

            setCardModalOpen(
                true,
            )
        }

    const openEditCard =
        (
            card,
        ) => {
            setEditingCard(
                card,
            )

            cardForm.setFieldsValue({
                name:
                    card.name ||
                    '',

                imageUrl:
                    card.imageAUrl ||
                    card.imageBUrl ||
                    '',

                isActive:
                    card.isActive !==
                    false,
            })

            setCardModalOpen(
                true,
            )
        }

    const closeModal =
        () => {
            setCardModalOpen(
                false,
            )

            setEditingCard(
                null,
            )

            cardForm.resetFields()
        }

    const uploadCardImage =
        async ({
            file,
            onSuccess,
            onError,
        }) => {
            try {
                setUploadingCard(
                    true,
                )

                const response =
                    await UploadService.image(
                        file,
                    )

                const data =
                    response?.data ||
                    response

                if (
                    !data?.url
                ) {
                    throw new Error(
                        'UPLOAD_URL_NOT_RETURNED',
                    )
                }

                cardForm.setFieldsValue({
                    imageUrl:
                        data.url,
                })

                message.success(
                    'Imagem enviada.',
                )

                onSuccess?.(
                    data,
                )
            } catch (error) {
                console.error(
                    'Erro no upload:',
                    error,
                )

                message.error(
                    'Erro ao enviar imagem.',
                )

                onError?.(
                    error,
                )
            } finally {
                setUploadingCard(
                    false,
                )
            }
        }

    const saveCard =
        async () => {
            if (
                !selectedGameId
            ) {
                return
            }

            try {
                const values =
                    await cardForm.validateFields()

                setSavingCard(
                    true,
                )

                const payload = {
                    name:
                        values.name ||
                        null,

                    imageUrl:
                        values.imageUrl,

                    isActive:
                        values.isActive !==
                        false,
                }

                if (
                    editingCard
                ) {
                    await axios.put(
                        `${API_BASE_URL}/memory-games/${selectedGameId}/cards/${editingCard.id}`,

                        payload,

                        axiosConfig,
                    )

                    message.success(
                        'Carta atualizada.',
                    )
                } else {
                    await axios.post(
                        `${API_BASE_URL}/memory-games/${selectedGameId}/cards`,

                        payload,

                        axiosConfig,
                    )

                    message.success(
                        'Carta adicionada.',
                    )
                }

                closeModal()

                await reload?.()
            } catch (error) {
                if (
                    error?.errorFields
                ) {
                    return
                }

                console.error(
                    'Erro ao salvar carta:',
                    error,
                )

                message.error(
                    error?.response?.data?.message ||
                    'Não foi possível salvar a carta.',
                )
            } finally {
                setSavingCard(
                    false,
                )
            }
        }

    const deleteCard =
        async (
            cardId,
        ) => {
            try {
                await axios.delete(
                    `${API_BASE_URL}/memory-games/${selectedGameId}/cards/${cardId}`,

                    axiosConfig,
                )

                message.success(
                    'Carta removida.',
                )

                await reload?.()
            } catch (error) {
                console.error(
                    'Erro ao excluir:',
                    error,
                )

                message.error(
                    error?.response?.data?.message ||
                    'Não foi possível excluir a carta.',
                )
            }
        }

    // ======================================================
    // COLUNAS
    // ======================================================

    const columns = [
        {
            title:
                '#',

            width:
                70,

            render:
                (
                    _,
                    __,
                    index,
                ) =>
                    index +
                    1,
        },

        {
            title:
                'Imagem da carta',

            width:
                220,

            render:
                (
                    _,
                    record,
                ) => {
                    const imageUrl =
                        record.imageAUrl ||
                        record.imageBUrl

                    if (
                        !imageUrl
                    ) {
                        return '-'
                    }

                    return (
                        <Image
                            src={getImageUrl(
                                imageUrl,
                            )}

                            preview={
                                true
                            }

                            width={
                                64
                            }

                            height={
                                64
                            }

                            style={{
                                objectFit:
                                    'contain',

                                borderRadius:
                                    8,

                                border:
                                    '1px solid #e8e8e8',

                                background:
                                    '#ffffff',
                            }}
                        />
                    )
                },
        },

        {
            title:
                'Nome (opcional)',

            dataIndex:
                'name',

            render:
                (
                    value,
                ) =>
                    value || (
                        <Text type="secondary">
                            Sem nome
                        </Text>
                    ),
        },

        {
            title:
                'Status',

            width:
                120,

            dataIndex:
                'isActive',

            render:
                (
                    value,
                ) =>
                    value ? (
                        <Tag color="green">
                            Ativo
                        </Tag>
                    ) : (
                        <Tag>
                            Inativo
                        </Tag>
                    ),
        },

        {
            title:
                'Ações',

            width:
                150,

            render:
                (
                    _,
                    record,
                ) => (
                    <Space>
                        <Button
                            icon={
                                <EditOutlined />
                            }

                            onClick={() =>
                                openEditCard(
                                    record,
                                )
                            }
                        />

                        <Popconfirm
                            title="Excluir esta carta?"
                            description="O par correspondente também deixará de aparecer no jogo."
                            okText="Excluir"
                            cancelText="Cancelar"
                            onConfirm={() =>
                                deleteCard(
                                    record.id,
                                )
                            }
                        >
                            <Button
                                danger
                                icon={
                                    <DeleteOutlined />
                                }
                            />
                        </Popconfirm>
                    </Space>
                ),
        },
    ]

    return (
        <>
            <Card
                title="Configuração das cartas"
                style={{
                    marginBottom:
                        16,
                }}
            >
                <Text type="secondary">
                    Configure uma imagem única para o verso e depois envie apenas uma imagem para cada par.
                </Text>

                <Row
                    gutter={[
                        24,
                        24,
                    ]}
                    style={{
                        marginTop:
                            20,
                    }}
                >
                    <Col
                        xs={24}
                        lg={12}
                    >
                        <Card
                            size="small"
                            title="Verso das cartas (comum a todas)"
                        >
                            <Text type="secondary">
                                Esta imagem aparecerá enquanto as cartas estiverem fechadas.
                            </Text>

                            <div
                                style={{
                                    display:
                                        'flex',

                                    alignItems:
                                        'center',

                                    gap:
                                        18,

                                    marginTop:
                                        18,
                                }}
                            >
                                <div
                                    style={{
                                        width:
                                            90,

                                        height:
                                            110,

                                        display:
                                            'flex',

                                        alignItems:
                                            'center',

                                        justifyContent:
                                            'center',

                                        border:
                                            '1px solid #e5e7eb',

                                        borderRadius:
                                            12,

                                        background:
                                            config?.cardBackColor ||
                                            '#1677ff',

                                        overflow:
                                            'hidden',
                                    }}
                                >
                                    {config?.cardBackImageUrl ? (
                                        <Image
                                            src={getImageUrl(
                                                config.cardBackImageUrl,
                                            )}

                                            preview={
                                                true
                                            }

                                            width={
                                                90
                                            }

                                            height={
                                                110
                                            }

                                            style={{
                                                objectFit:
                                                    'cover',
                                            }}
                                        />
                                    ) : (
                                        <span
                                            style={{
                                                color:
                                                    '#ffffff',

                                                fontSize:
                                                    34,

                                                fontWeight:
                                                    900,
                                            }}
                                        >
                                            ?
                                        </span>
                                    )}
                                </div>

                                <Space
                                    direction="vertical"
                                >
                                    <Upload
                                        accept=".png,.jpg,.jpeg,.webp"
                                        showUploadList={
                                            false
                                        }
                                        customRequest={
                                            uploadBackImage
                                        }
                                    >
                                        <Button
                                            icon={
                                                <UploadOutlined />
                                            }
                                            loading={
                                                uploadingBack ||
                                                savingBack
                                            }
                                        >
                                            {config?.cardBackImageUrl
                                                ? 'Alterar verso'
                                                : 'Enviar verso'}
                                        </Button>
                                    </Upload>

                                    {config?.cardBackImageUrl && (
                                        <Button
                                            danger
                                            loading={
                                                savingBack
                                            }
                                            onClick={() =>
                                                saveBackImage(
                                                    null,
                                                )
                                            }
                                        >
                                            Remover
                                        </Button>
                                    )}
                                </Space>
                            </div>
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        lg={12}
                    >
                        <Alert
                            showIcon
                            type="info"
                            message="Como funciona"
                            description={
                                <div>
                                    <div>
                                        ✓ Defina um único verso para todas as cartas.
                                    </div>

                                    <div>
                                        ✓ Cadastre uma imagem por par.
                                    </div>

                                    <div>
                                        ✓ A programação duplica cada imagem automaticamente.
                                    </div>

                                    <div>
                                        ✓ {totalBoardCards} cartas = {requiredCards} imagens.
                                    </div>
                                </div>
                            }
                        />
                    </Col>
                </Row>
            </Card>

            <Card
                title={
                    <Space>
                        <span>
                            Cartas do jogo
                        </span>

                        <Tag color="blue">
                            {activeCards.length} / {requiredCards}
                        </Tag>
                    </Space>
                }

                extra={
                    <Button
                        type="primary"
                        icon={
                            <PlusOutlined />
                        }
                        disabled={
                            !selectedGameId ||
                            !canAddCard
                        }
                        onClick={
                            openNewCard
                        }
                    >
                        Adicionar carta
                    </Button>
                }
            >
                <Text
                    type="secondary"
                    style={{
                        display:
                            'block',

                        marginBottom:
                            18,
                    }}
                >
                    Para um jogo com {totalBoardCards} cartas, envie {requiredCards} imagens. Cada imagem aparecerá duas vezes no tabuleiro.
                </Text>

                <Table
                    rowKey="id"
                    columns={
                        columns
                    }
                    dataSource={
                        cards
                    }
                    pagination={
                        false
                    }
                    locale={{
                        emptyText:
                            'Nenhuma carta cadastrada.',
                    }}
                />

                <Alert
                    style={{
                        marginTop:
                            20,
                    }}
                    type={
                        activeCards.length ===
                        requiredCards
                            ? 'success'
                            : 'warning'
                    }
                    showIcon
                    message={
                        activeCards.length ===
                        requiredCards
                            ? `Configuração completa: ${requiredCards} imagens formarão ${totalBoardCards} cartas.`
                            : `Faltam ${requiredCards - activeCards.length} imagem(ns) para completar este jogo.`
                    }
                />
            </Card>

            <Modal
                open={
                    cardModalOpen
                }
                title={
                    editingCard
                        ? 'Editar carta'
                        : 'Adicionar carta'
                }
                onCancel={
                    closeModal
                }
                onOk={
                    saveCard
                }
                okText="Salvar"
                cancelText="Cancelar"
                confirmLoading={
                    savingCard
                }
                destroyOnClose
            >
                <Form
                    form={
                        cardForm
                    }
                    layout="vertical"
                >
                    <Form.Item
                        label="Nome da carta"
                        name="name"
                    >
                        <Input
                            placeholder="Ex.: Garrafa amarela"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Imagem"
                        name="imageUrl"
                        rules={[
                            {
                                required:
                                    true,

                                message:
                                    'Envie a imagem da carta.',
                            },
                        ]}
                    >
                        <Input
                            placeholder="/uploads/carta.png"
                        />
                    </Form.Item>

                    <Upload
                        accept=".png,.jpg,.jpeg,.webp"
                        showUploadList={
                            false
                        }
                        customRequest={
                            uploadCardImage
                        }
                    >
                        <Button
                            icon={
                                <UploadOutlined />
                            }
                            loading={
                                uploadingCard
                            }
                        >
                            Enviar imagem
                        </Button>
                    </Upload>

                    <Form.Item
                        shouldUpdate
                        noStyle
                    >
                        {({
                            getFieldValue,
                        }) => {
                            const imageUrl =
                                getFieldValue(
                                    'imageUrl',
                                )

                            if (
                                !imageUrl
                            ) {
                                return null
                            }

                            return (
                                <div
                                    style={{
                                        marginTop:
                                            18,
                                    }}
                                >
                                    <Image
                                        src={getImageUrl(
                                            imageUrl,
                                        )}

                                        width={
                                            120
                                        }

                                        height={
                                            120
                                        }

                                        style={{
                                            objectFit:
                                                'contain',

                                            border:
                                                '1px solid #e5e7eb',

                                            borderRadius:
                                                12,

                                            padding:
                                                8,
                                        }}
                                    />
                                </div>
                            )
                        }}
                    </Form.Item>

                    <Form.Item
                        label="Ativa"
                        name="isActive"
                        valuePropName="checked"
                        style={{
                            marginTop:
                                18,
                        }}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}

export default CardsTab